<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useInterval } from "@vueuse/core";
import { usePlaceStore } from "@/modules/place-module";

const currentSlide = ref(0);
const { counter, reset, pause, resume } = useInterval(100, { controls: true });
const progress = computed(() => {
  return (counter.value / 40) * 100;
});
const placeStore = usePlaceStore();
const images = computed(() => placeStore.place?.place.images);
const logo = computed(() => placeStore.place?.place.logo_src);

const prevSlide = () => {
  reset();

  if (currentSlide.value === 0) {
    currentSlide.value = images.value.length - 1;
    return;
  }

  currentSlide.value--;
};

const nextSlide = () => {
  reset();

  if (currentSlide.value === images.value.length - 1) {
    currentSlide.value = 0;
    return;
  }

  currentSlide.value++;
};

watch(counter, (newValue) => {
  if (newValue === 41) {
    nextSlide();
  }
});

watch(
  () => images,
  (newValue) => {
    currentSlide.value = 0;
    reset();
    resume();

    if (newValue.length <= 1) {
      pause();
    }
  }
);

onMounted(() => {
  if (images.value.length <= 1) {
    pause();
  }
});
</script>

<template>
  <section class="place-slider">
    <div
      class="place-slider__slide"
      :style="{
        backgroundImage: images.length ? `url(${images[currentSlide]})` : '',
      }"
    >
      <button v-if="images.length > 1" type="button" @click="prevSlide" />
      <button v-if="images.length > 1" type="button" @click="nextSlide" />
      <span :style="{ backgroundImage: `url(${logo})` }" />
    </div>

    <div v-if="images.length > 1" class="place-slider__controls">
      <div
        v-for="(slide, index) in images"
        :key="index"
        class="place-slider__control"
      >
        <span>
          <span
            v-if="currentSlide === index"
            :style="{ width: `${progress}%` }"
          />
        </span>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.place-slider {
  width: 100%;

  &__controls {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin: calc($base-space * 2) 0 0;
    padding: 0 calc($base-space * 8);

    > a {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 0 0 calc($base-space * 4);

      > .icon {
        background-color: var(--color-semantic-content-normal-contrast-primary);
      }
    }
  }

  &__control {
    padding: calc($base-space * 2);
    width: 100%;
    position: relative;

    > span {
      height: 4px;
      border-radius: 4px;

      background-color: var(--color-semantic-background-normal-tertiary);
      width: 100%;
      position: relative;
      display: block;

      > span {
        display: block;
        position: absolute;
        left: 0;
        bottom: 0;
        top: 0;
        border-radius: 20px;
        background-color: var(--color-semantic-background-normal-contrast);
        transition: 0.1s ease-in;
      }
    }
  }

  &__slide {
    position: relative;
    width: 100%;
    height: 270px;
    @include centerBackgroundImageCover();

    @media (hover: hover) {
      &:hover {
        > span {
          opacity: 0;
        }
      }
    }

    > span {
      width: 72px;
      height: 72px;
      border-radius: 12px;
      position: absolute;
      bottom: 24px;
      left: 32px;
      opacity: 1;
      @include centerBackgroundImageCover();
      transition: all 0.3s ease-out;
    }

    > button {
      position: absolute;
      top: 0;
      bottom: 0;
      z-index: 2;
      background: none;
      width: 120px;

      &:first-of-type {
        left: 0;
      }

      &:last-of-type {
        right: 0;
      }
    }
  }
}
</style>
